import { useEffect, useState } from 'react'
import posthog from 'posthog-js'
import { toast } from 'react-toastify'

import {
    canRequestContact,
    isMiniapp,
    loginWithSharedContact,
    requestPhoneFromMessenger,
    waitForContactLink,
} from 'modules/miniapp'
import { POST_LOGIN_PATH } from 'utilities/navigation'

export type ShareStage = 'idle' | 'requesting' | 'waiting' | 'fallback'

/** Which of the two buttons drove the flow — the funnel is split on it. */
export type ShareVariant = 'hero' | 'inline'

// ~20 seconds. Long enough for the messenger to deliver the contact to our bot
// and for the webhook to write the link; short enough that nobody assumes the
// button is broken.
const WAIT_ATTEMPTS = 13
const WAIT_DELAY_MS = 1500

interface Options {
    variant: ShareVariant
    /** Fill the phone field WITHOUT sending anything. */
    onPrefill?: (phone: string) => void
    /** What to tell someone whose messenger handed back no number at all. */
    declinedMessage: string
}

/**
 * "Sign in with the number on this messenger account" — the state machine.
 *
 * Shared by the big «ورود با ایتا» button and the small one under the phone
 * field, so the two can never drift apart on the part that matters.
 *
 * Crucially this does NOT send an SMS. Sharing the number is often enough to
 * log in on its own, and firing an OTP up front would charge us for a message
 * the user never needs and drop them on a code screen they should not be on.
 *
 * Order of attempts:
 *   1. A *signed* contact payload (Eitaa, Telegram) — proven, straight in.
 *   2. The messenger delivers the contact to our BOT (Bale), which links the
 *      account behind the page — so we wait and poll for it.
 *   3. Only if both fail does the caller get `stage === 'fallback'` and a
 *      number to offer the SMS code for, which is the first moment an SMS can
 *      be sent — and only on an explicit tap.
 */
export const useShareNumber = ({
    variant,
    onPrefill,
    declinedMessage,
}: Options) => {
    // null until the SDK has answered, so a caller can hold its layout instead
    // of flashing the phone form at someone about to get the one-tap button.
    const [available, setAvailable] = useState<boolean | null>(
        isMiniapp() ? null : false
    )
    const [stage, setStage] = useState<ShareStage>('idle')
    const [phone, setPhone] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false
        if (!isMiniapp()) return
        canRequestContact()
            .then((ok) => {
                if (!cancelled) setAvailable(ok)
            })
            .catch(() => {
                if (!cancelled) setAvailable(false)
            })
        return () => {
            cancelled = true
        }
    }, [])

    // Full reload so every consumer of auth state picks up the new token,
    // matching what the OTP path does.
    //
    // The capture has to happen BEFORE the reload, and it is the only record
    // that this button ever works: the funnel had `tapped` and `declined` but
    // no success step at all, so the whole feature read as broken whenever
    // declines outnumbered fills.
    const signedIn = (route: 'signed_payload' | 'bot_link') => {
        posthog.capture('miniapp_share_number_succeeded', { route, variant })
        window.location.replace(POST_LOGIN_PATH)
    }

    const share = async () => {
        setStage('requesting')
        posthog.capture('miniapp_share_number_tapped', { variant })
        const result = await requestPhoneFromMessenger()

        // (1) Signed payload is proof — log straight in, no SMS.
        if (result?.signedResponse) {
            if (await loginWithSharedContact(result.signedResponse)) {
                return signedIn('signed_payload')
            }
        }

        // Keep the number so the fallback below is one tap, but send nothing.
        if (result?.phone) {
            setPhone(result.phone)
            onPrefill?.(result.phone)
            posthog.capture('miniapp_share_number_filled', { variant })
        }

        // (2) Wait for the contact to reach our bot and the link to appear.
        setStage('waiting')
        const linked = await waitForContactLink(WAIT_ATTEMPTS, WAIT_DELAY_MS)
        if (linked) return signedIn('bot_link')

        // (3a) We never got a number, so there is nothing to offer an SMS
        // for. Offering a "log in by SMS" button here would be a button whose
        // sole action is to say "type your number".
        if (!result?.phone) {
            posthog.capture('miniapp_share_number_declined', { variant })
            toast.info(declinedMessage)
            setStage('idle')
            return
        }

        // (3b) We have the number but could not prove it. Offer the SMS route
        // rather than taking it for them — the only place an SMS gets sent.
        setStage('fallback')
    }

    return { available, stage, phone, share }
}
